import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Building2, ShieldCheck } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";
import FoxLogo from "@/components/FoxLogo";

const SIDE_IMG = "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?auto=format&fit=crop&w=1400&q=80";

const PROPERTY_TYPE_KEYS = ["hotel", "bnb", "affittacamere", "agriturismo", "campeggio", "casa_vacanza"];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Signup() {
  const { t } = useTranslation();
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "", email: "", password: "",
    property_name: "", property_type: "hotel",
    comune: "", provincia: "",
    tourist_tax_rate: 2.0, tourist_tax_max_nights: 7,
  });
  const [loading, setLoading] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) {
      toast.error("Devi accettare l'informativa privacy per registrarti");
      return;
    }
    setLoading(true);
    try {
      await signup({
        ...form,
        tourist_tax_rate: Number(form.tourist_tax_rate),
        tourist_tax_max_nights: Number(form.tourist_tax_max_nights),
        privacy_accepted: privacyAccepted,
        marketing_accepted: marketingAccepted,
      });
      toast.success(t("auth.account_created"));
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.signup_error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="flex items-center justify-center p-8 order-2 md:order-1 relative">
        <div className="absolute top-6 left-6"><LanguageSelector /></div>
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="signup-form">
          <div className="md:hidden flex items-center gap-2 mb-6">
            <FoxLogo size={28} />
            <span className="font-heading font-semibold text-2xl">Xenia</span>
          </div>
          <h1 className="font-heading font-semibold text-4xl tracking-tight mb-2">{t("auth.signup_title")}</h1>
          <p className="text-slate-600 text-sm mb-6">{t("auth.signup_sub")}</p>

          <button type="button" onClick={googleLogin} data-testid="google-signup"
            className="w-full flex items-center justify-center gap-3 border border-slate-300 bg-white px-4 py-3 text-sm hover:bg-slate-50 transition-colors mb-5">
            <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {t("auth.google_button")}
          </button>
          <div className="mb-5 flex items-center gap-3 text-xs text-slate-500">
            <span className="flex-1 h-px bg-stone-200"></span>{t("auth.or")}<span className="flex-1 h-px bg-stone-200"></span>
          </div>

          <Field label={t("auth.full_name").toUpperCase()}>
            <input data-testid="signup-fullname" required value={form.full_name} onChange={set("full_name")} className={inputCls} />
          </Field>

          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="col-span-2">
              <Field label={t("auth.property_name").toUpperCase()}>
                <input data-testid="signup-property-name" required value={form.property_name} onChange={set("property_name")} className={inputCls} />
              </Field>
            </div>
            <Field label={t("auth.property_type").toUpperCase()}>
              <select data-testid="signup-property-type" value={form.property_type} onChange={set("property_type")} className={inputCls}>
                {PROPERTY_TYPE_KEYS.map(k => <option key={k} value={k}>{t(`property_types.${k}`)}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="col-span-2">
              <Field label={t("auth.comune").toUpperCase()}>
                <input data-testid="signup-comune" required value={form.comune} onChange={set("comune")} className={inputCls} />
              </Field>
            </div>
            <Field label={t("auth.provincia").toUpperCase()}>
              <input data-testid="signup-provincia" maxLength={2} value={form.provincia}
                onChange={(e) => setForm(f => ({ ...f, provincia: e.target.value.toUpperCase() }))}
                className={`${inputCls} uppercase`} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <Field label={t("auth.tax_rate").toUpperCase()}>
              <input type="number" step="0.01" min={0} value={form.tourist_tax_rate} onChange={set("tourist_tax_rate")} className={inputCls} />
            </Field>
            <Field label={t("auth.max_nights").toUpperCase()}>
              <input type="number" min={1} value={form.tourist_tax_max_nights} onChange={set("tourist_tax_max_nights")} className={inputCls} />
            </Field>
          </div>

          <div className="mt-4">
            <Field label={t("common.email").toUpperCase()}>
              <input data-testid="signup-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
            </Field>
          </div>

          <div className="mt-4">
            <Field label={t("common.password").toUpperCase()}>
              <input data-testid="signup-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className={inputCls} />
            </Field>
          </div>

          <div className="mt-6 space-y-3 border-t border-slate-200 pt-5">
            <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                required
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                data-testid="signup-privacy-consent"
                className="mt-1 w-4 h-4 accent-blue-600"
              />
              <span>
                Ho letto e accetto l'<Link to="/privacy" target="_blank" className="text-blue-600 underline underline-offset-2" data-testid="privacy-link">Informativa Privacy</Link> ai sensi del Reg. UE 2016/679 (GDPR) e acconsento al trattamento dei miei dati personali per la fornitura del servizio. <span className="text-red-700">*</span>
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={marketingAccepted}
                onChange={(e) => setMarketingAccepted(e.target.checked)}
                data-testid="signup-marketing-consent"
                className="mt-1 w-4 h-4 accent-blue-600"
              />
              <span>
                Acconsento facoltativamente all'invio di comunicazioni commerciali e novità di prodotto. Posso revocare il consenso in qualsiasi momento.
              </span>
            </label>
          </div>

          <button data-testid="signup-submit" disabled={loading || !privacyAccepted}
            className="mt-6 w-full bg-gradient-to-br from-blue-600 to-blue-700 text-white px-4 py-3 rounded-lg hover:from-blue-500 hover:to-blue-600 shadow-[0_8px_20px_-6px_rgba(37,99,235,0.4)] font-semibold transition-all text-sm disabled:opacity-60">
            {loading ? t("auth.creating") : t("auth.create_account")}
          </button>

          <div className="mt-4 inline-flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
            {t("auth.security_cue", "Dati protetti · GDPR · Alloggiati Web certificato")}
          </div>
          <p className="mt-6 text-sm text-slate-600">
            {t("auth.already_registered")}{" "}
            <Link to="/login" data-testid="goto-login" className="text-blue-600 hover:underline underline-offset-4">{t("nav.login")}</Link>
          </p>
        </form>
      </div>
      <div className="hidden md:block relative order-1 md:order-2">
        <img src={SIDE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-slate-50">
          <Link to="/" className="font-heading font-semibold text-2xl inline-flex items-center gap-2"><FoxLogo size={32} />Xenia<span className="text-sky-300">.</span></Link>
          <div>
            <p className="text-xs tracked text-sky-300 mb-4">{t("auth.access_label_signup")}</p>
            <h2 className="font-heading font-semibold text-4xl tracking-tight max-w-md">{t("auth.side_quote_signup")}</h2>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 bg-white text-sm";
function Field({ label, children }) {
  return (<label className="block"><span className="block text-xs tracked text-slate-500 mb-2">{label}</span>{children}</label>);
}
